import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import ClearIcon from '@mui/icons-material/Clear';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Tooltip from '../Tooltip/Tooltip';
import Typography from '../Typography/Typography';
import { baseSelectButtonClasses as classes } from './baseSelectButtonClasses';
import {
  BaseSelectButtonRoot,
  ErrorLabelContainer,
  ErrorMessage,
  HelperMessage,
  IconsContainer,
  LabelContainer,
  SelectButtonContainer,
  FooterContainer
} from './BaseSelectButton.style';

const BaseSelectButton = forwardRef(
  (
    {
      isMultiSelectionMode,
      errorText,
      helperText,
      shouldRenderClearIcon,
      clearSelection,
      disabled,
      readOnly,
      children,
      ExpandIcon,
      label,
      size,
      isError,
      hasValues,
      FormLabelProps,
      TooltipProps,
      className,
      sx, // should be explicitly destructed as to enable styling of both button, label and error text
      ...rest
    },
    ref
  ) => {
    const qaComponentType = isMultiSelectionMode ? 'multi-select' : 'select';
    const hasTooltip = TooltipProps !== undefined;
    const showAdditionalText = errorText || helperText;

    const handleClearIconKeyDown = event => {
      const { key } = event;
      if (key === 'Enter' || key === ' ') {
        clearSelection(event);
      }
    };

    return (
      <BaseSelectButtonRoot className={className} sx={sx}>
        {label && (
          <LabelContainer
            className={classes.formLabel}
            data-qa-id={`${qaComponentType}-form-label`}
            hasTooltip={hasTooltip}
            {...FormLabelProps}>
            <Typography
              className={classes.label}
              variant="body2"
              data-qa-id={`${qaComponentType}-label`}>
              {label}
            </Typography>
            {hasTooltip && (
              <Tooltip placement="top" variant="info" {...TooltipProps}>
                <HelpOutlineIcon
                  className={classes.helpIcon}
                  fontSize="small"
                />
              </Tooltip>
            )}
          </LabelContainer>
        )}
        <SelectButtonContainer
          container
          component="button"
          type="button"
          justifyContent="space-between"
          alignItems="center"
          ref={ref}
          disabled={disabled}
          data-readonly={readOnly}
          isError={isError}
          hasValues={hasValues}
          readOnly={readOnly}
          size={size}
          data-qa-id={`${qaComponentType}-button`}
          className={clsx(classes.selectButton, classes[size])}
          {...rest}>
          {children}
          {!readOnly && (
            <IconsContainer
              item
              container
              isClearable={shouldRenderClearIcon}
              disabled={disabled}
              className={classes.iconsWrapper}>
              {shouldRenderClearIcon && (
                <ClearIcon
                  className={classes.clearIcon}
                  data-qa-id="clear-icon"
                  fontSize="small"
                  color={disabled ? 'disabled' : 'action'}
                  role="button"
                  aria-label="clear"
                  onKeyDown={handleClearIconKeyDown}
                  onClick={clearSelection}
                />
              )}
              <ExpandIcon
                disabled={disabled}
                color={disabled ? 'disabled' : 'action'}
                className={classes.expandIcon}
              />
            </IconsContainer>
          )}
        </SelectButtonContainer>
        {showAdditionalText && (
          <FooterContainer
            container
            wrap="nowrap"
            alignItems="flex-start"
            size={size}
            className={classes.footer}
            data-qa-id={`${qaComponentType}-footer`}>
            {errorText && (
              <ErrorLabelContainer
                className={classes.errorContainer}
                data-qa-id={`${qaComponentType}-error-container`}>
                <ErrorOutlineIcon
                  fontSize="small"
                  color="error"
                  data-qa-id={`${qaComponentType}-error-icon`}
                />
                <ErrorMessage
                  className={classes.textError}
                  color="error"
                  data-qa-id={`${qaComponentType}-error-text`}
                  variant="caption">
                  {errorText}
                </ErrorMessage>
              </ErrorLabelContainer>
            )}
            {helperText && (
              <HelperMessage
                data-qa-id={`${qaComponentType}-helper-text`}
                variant="caption"
                className={classes.helperText}>
                {helperText}
              </HelperMessage>
            )}
          </FooterContainer>
        )}
      </BaseSelectButtonRoot>
    );
  }
);

BaseSelectButton.propTypes = {
  isMultiSelectionMode: PropTypes.bool,
  errorText: PropTypes.string,
  helperText: PropTypes.string,
  shouldRenderClearIcon: PropTypes.bool,
  clearSelection: PropTypes.func,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  children: PropTypes.node,
  ExpandIcon: PropTypes.elementType,
  label: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium', 'large', 'fullWidth']),
  isError: PropTypes.bool,
  hasValues: PropTypes.bool,
  FormLabelProps: PropTypes.object,
  TooltipProps: PropTypes.object
};

export default BaseSelectButton;
